import { useState, useEffect } from 'react';
import {
  setCurrencyDepreciation, getAllFXRates, normaliseCurrency,
  depreciationPercentToFraction, depreciationFractionToPercent,
  DEPRECIATION_CONFIRM_ABOVE,
} from '../../../services/fxRateService';
import { getAllCountries } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import Button from '../../shared/UI/Button';

// The percentage at/above which the admin must confirm the value. Derived from
// the service's fraction threshold so the two never drift.
const CONFIRM_ABOVE_PCT = DEPRECIATION_CONFIRM_ABOVE * 100; // 50

/**
 * Set the per-currency annual depreciation assumption used by the Future USD
 * CAGR. Value is ENTERED and DISPLAYED as a percentage (10 = 10%/yr) but STORED
 * as a fraction (0.10) — the conversion lives in fxRateService so it is pinned
 * by a test. A value >= 50%/yr requires an explicit confirmation before it will
 * save, so a percentage typo (90 for 9) cannot pass silently.
 */
export default function FXDepreciationForm({ onSaved, onCancel }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    currency: '', percent: '', reason: '', confirmHigh: false,
  });
  const [suggestions, setSuggestions] = useState([]);
  const [existing, setExisting] = useState({}); // currency -> stored fraction
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Suggestions + current stored values. Codes come from the countries registry
  // and from currencies already carrying rates — never a hardcoded list (OI-018).
  useEffect(() => {
    Promise.all([getAllCountries(), getAllFXRates()])
      .then(([countries, fx]) => {
        const codes = new Set();
        countries.forEach(c => { if (c.currency_code) codes.add(c.currency_code); });
        const dep = {};
        fx.forEach(d => {
          if (d.currency_code) codes.add(d.currency_code);
          if (typeof d.usd_depreciation_annual === 'number') {
            dep[d.currency_code || d.id] = d.usd_depreciation_annual;
          }
        });
        setSuggestions([...codes].sort());
        setExisting(dep);
      })
      .catch(() => { setSuggestions([]); setExisting({}); });
  }, []);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  // Prefill the percent when a known currency with a stored value is selected,
  // so the admin edits from the current value rather than a blank field.
  function onCurrency(raw) {
    const code = raw.toUpperCase();
    const stored = existing[normaliseCurrency(code)];
    setForm(prev => ({
      ...prev,
      currency: code,
      percent: typeof stored === 'number'
        ? String(depreciationFractionToPercent(stored))
        : prev.percent,
    }));
    if (errors.currency) setErrors(prev => ({ ...prev, currency: '' }));
  }

  const code = normaliseCurrency(form.currency);
  const currencyLabel = code || 'this currency';
  const percentNum = Number(form.percent);
  const isHigh = isFinite(percentNum) &&
    percentNum >= CONFIRM_ABOVE_PCT && percentNum < 100;
  const currentPct = typeof existing[code] === 'number'
    ? depreciationFractionToPercent(existing[code]) : null;

  function validate() {
    const e = {};
    if (!String(form.currency).trim()) e.currency = 'Currency is required';
    if (String(form.percent).trim() === '') {
      e.percent = 'Depreciation is required';
    } else if (!isFinite(percentNum) || percentNum < 0) {
      e.percent = 'Enter a non-negative percentage, e.g. 10';
    } else if (percentNum >= 100) {
      e.percent = 'Must be below 100%/yr';
    }
    if (isHigh && !form.confirmHigh) {
      e.confirmHigh = 'Confirm this unusually high rate to proceed';
    }
    return e;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    setResult(null);
    try {
      const res = await setCurrencyDepreciation({
        currency: form.currency,
        depreciation: depreciationPercentToFraction(form.percent),
        confirmHigh: form.confirmHigh,
        reason: form.reason,
        performedBy: user.uid,
      });
      setResult(res);
      setTimeout(() => onSaved(), 1400);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {result && (
        <div className="alert alert-success">
          Saved {result.currency} depreciation at{' '}
          {depreciationFractionToPercent(result.depreciation)}%/yr
          {result.unchanged ? ' (unchanged).' : '.'}
        </div>
      )}

      <div className="alert alert-info" style={{ fontSize: 13 }}>
        This assumption projects future year-end rates forward for the
        {' '}<strong>Future USD CAGR</strong>. It is the annual loss of value of the
        currency against USD: 10%/yr means the USD value of 1 unit falls 10% each
        year, so the stored year-end rate declines by 10% per projected year.
      </div>

      <div className="form-grid mb-4">
        <FormField label="Currency" required error={errors.currency}
          hint="Any 3-letter ISO code. Known codes are suggested, but not required.">
          <input
            className={`form-input ${errors.currency ? 'error' : ''}`}
            list="fx-dep-currency-suggestions"
            value={form.currency}
            maxLength={3}
            placeholder="e.g. GHS"
            onChange={e => onCurrency(e.target.value)}
          />
        </FormField>
        <datalist id="fx-dep-currency-suggestions">
          {suggestions.map(c => <option key={c} value={c} />)}
        </datalist>

        <FormField
          label="Annual depreciation (%)"
          required
          error={errors.percent}
          hint={`Enter as a percentage per year — e.g. 10 for 10%/yr. Stored as a fraction (10 → 0.10). This is the annual loss of value of ${currencyLabel} against USD.`}
        >
          <input
            type="number"
            step="any"
            min="0"
            className={`form-input ${errors.percent ? 'error' : ''}`}
            value={form.percent}
            placeholder="e.g. 10"
            onChange={e => set('percent', e.target.value)}
          />
        </FormField>

        {currentPct != null && (
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Current stored value for {code}: <strong>{currentPct}%/yr</strong>.
            Saving a change archives the previous value and increments the
            document version.
          </div>
        )}

        <FormField label="Reason for change"
          hint="Optional. Recorded in the document history and the audit log.">
          <input className="form-input" value={form.reason}
            placeholder="e.g. Updated to reflect the latest IMF projection"
            onChange={e => set('reason', e.target.value)} />
        </FormField>

        {isHigh && (
          <div className="alert alert-warning">
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.confirmHigh}
                onChange={e => set('confirmHigh', e.target.checked)} />
              <span>
                <strong>{percentNum}%/yr is unusually high.</strong> A common cause
                is a percentage typo (e.g. 90 instead of 9). Tick to confirm this
                is intentional.
              </span>
            </label>
            {errors.confirmHigh && (
              <div style={{ color: 'var(--color-error)', marginTop: 4 }}>
                {errors.confirmHigh}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex-between">
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" loading={saving}>
          Save Depreciation
        </Button>
      </div>
    </form>
  );
}
